import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { PngGeneratorService } from 'src/app/services/pngGenerator/png-generator.service';
import { map, Observable } from 'rxjs';
import { TinyUrlResponse } from '../update-device-screen/update-device-screen.component';
import { QRCodeService } from 'src/app/services/qrcode/qrcode.service';

export interface Device {
    address: string;
    archive: number;
    denomination_pos: string;
    battery_voltage: number;
    created_at: string;
    n_location: string;
    uuid: string;
    push_switch_state: number;
    screen_number: number;
    temperature: number;
    updated_at: string;
    status?: number;
    rssi?: number;
}

interface DeviceInBDD {
    _id: number;
    uuid: string;
    id_point_of_sale: number;
    n_location: number;
    id_user: number;
    date: string;
    last_update: string;
    archive: number;
    manufacturer: number;
}

@Component({
    selector: 'app-device-list',
    templateUrl: './device-list.component.html'
})
export class DeviceListComponent implements OnInit {
    devices: Device[] = [];
    newDevices: { uuid: string; n_location: number }[] = [];
    loading = false;
    errorMessage = '';
    urlBase = environment.apiUrl;
    id_user = '87141';
    tinyurlApiKey = environment.tinyUrl.tinyurlApiKey;
    tinyurlApiUrl = environment.tinyUrl.tinyurlUrl;

    constructor(
        private http: HttpClient,
        private router: Router,
        private pngGenerator: PngGeneratorService,
        private qrCodeService: QRCodeService,
    ) { }

    ngOnInit(): void {
        this.fetchDevices();
    }

    onError(place: string, error: unknown): void {
        if (error instanceof Error)
            console.error(place, ":", error.message);
        else
            console.error(place, ":", error);
    }

    async getBaseApi<T>(endpoint: string, params: Record<string, unknown> = {}): Promise<T> {
        const queryParams = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
            if (value !== undefined && value !== null)
                queryParams.set(key, String(value));
        }
        const url = `${this.urlBase}${endpoint}?${queryParams.toString()}`;
        try {
            const res = await this.http.get<T>(url).toPromise();
            return res as T;
        } catch (err) {
            this.onError('API error:', err);
            throw err;
        }
    }

    async postBaseApi<T>(endpoint: string, param: Record<string, unknown> = {}): Promise<T> {
        try {
            const result = await this.http.post<T>(this.urlBase + endpoint, param, {
                headers: { "Content-Type": "application/json" }
            }).toPromise();
            return result as T;
        } catch (error) {
            this.onError(`POST ${endpoint}`, error);
            throw error;
        }
    }

    async fetchDevices(): Promise<void> {
        try {
            const body = {
                id_user: 87141,
            };
            this.loading = true;
            const response = await this.getBaseApi<{ devices: Device[] }>('get-devices', body)
            if (response) {
                this.devices = response.devices;
            }
        } catch (error: any) {
            switch (error.status) {
                case (0):
                    this.errorMessage = "Erreur de connexion au serveur. Vérifie ta connexion internet.";
                    break;
                case (403):
                    this.errorMessage = "Clé API manquante ou invalide.";
                    break;
                case (401):
                    this.errorMessage = "Authentification requise.";
                    break;
                case (400):
                    this.errorMessage = error.error?.error?.message ?? "Requête invalide.";
                    break;
                default:
                    this.errorMessage = `Erreur inattendue (${error.status}) : ${error.message}`;
            }
            console.error("Erreur lors de la récupération des appareils :", error);
        } finally {
            this.loading = false;
        };
    }

    async syncDevices(): Promise<void> {
        try {
            const body = {
                id_user: 87141,
                id_point_of_sale: 90680
            };
            this.loading = true;
            const response = await this.postBaseApi<{ new_devices: DeviceInBDD[] }>('sync-devices', body)
            if (response) {
                this.newDevices = response.new_devices;
            }
        } catch (error: any) {
            switch (error.status) {
                case (0):
                    this.errorMessage = "Erreur de connexion au serveur. Vérifie ta connexion internet.";
                    break;
                case (403):
                    this.errorMessage = "Clé API manquante ou invalide.";
                    break;
                case (401):
                    this.errorMessage = "Authentification requise.";
                    break;
                case (400):
                    this.errorMessage = error.error?.error?.message ?? "Requête invalide.";
                    break;
                default:
                    this.errorMessage = `Erreur inattendue (${error.status}) : ${error.message}`;
            }
            console.error("Erreur lors de la récupération des appareils :", error);
        } finally {
            await this.fetchDevices();
            this.loading = false;
        };
    }

    shortenUrl(longUrl: string): Observable<string> {
        const headers = new HttpHeaders({
            Authorization: `Bearer ${this.tinyurlApiKey}`,
            'Content-Type': 'application/json',
        });
        const body = {
            url: longUrl,
            domain: 'tiny.one', // ou tinyurl.com
        };
        const result = this.http.post<TinyUrlResponse>(this.tinyurlApiUrl, body, { headers }).pipe(
            map(res => res.data.tiny_url)
        );
        return result
    }

    async defaultQRencode(table_number: string, deviceId: string, id_user: string) {
    try {
        const tableNum = parseInt(table_number);
        const denom_pos = this.devices.find(d => d.uuid === deviceId)?.denomination_pos || 'inconnu';

        const noteUrlResponse = await this.qrCodeService
            .generateNoteUrl(206679, 'mja2njc5xzy5ngy', tableNum, denom_pos)
            .toPromise();
        
        const url_wn = noteUrlResponse?.url;
        if (!url_wn) throw new Error("URL manquante depuis generateNoteUrl");

        const textToEncode = `table ${table_number} pour payer scannez moi`;

        const shortenedUrl = await this.shortenUrl(url_wn).toPromise();
        if (shortenedUrl) {
            this.pngGenerator.generateDynamicTemplate(textToEncode, shortenedUrl, deviceId, id_user);
        }
    } catch (error) {
        this.onError("QR encode error", error);
    }
}


}
